import React, { useState } from 'react';
import { mutate } from './api';

export interface PartnerCompany { id: number; code: string }
export interface PartnerRow {
    id: number; name: string; oib: string | null; iban: string | null; bank_name: string | null;
    city: string | null; country_code: string | null;
    contact_name: string | null; contact_email: string | null; contact_phone: string | null;
    default_payment_model: string | null; default_payment_reference: string | null;
    companies: PartnerCompany[]; archived?: boolean;
}

/**
 * Drawer za kreiranje/uređivanje partnera (dobavljača). Obavezno: naziv, OIB, IBAN, ≥1 firma.
 *  Koristi se u admin Šifrarnici (m.112) i kod create-from-URA u likvidaturi.
 */
export function PartnerDrawer({ token, locale, partner, companies, prefillName, prefillCompanyId, onClose, onSaved, setError }: {
    token: string | null;
    locale: 'hr' | 'en';
    partner: PartnerRow | null;
    companies: { id: number; code: string; name: string }[];
    prefillName?: string;
    prefillCompanyId?: number | null;
    onClose: () => void;
    onSaved: (id: number) => void;
    setError: (s: string) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [f, setF] = useState({
        name: partner?.name ?? prefillName ?? '',
        oib: partner?.oib ?? '',
        iban: partner?.iban ?? '',
        bank_name: partner?.bank_name ?? '',
        city: partner?.city ?? '',
        country_code: partner?.country_code ?? 'HR',
        contact_name: partner?.contact_name ?? '',
        contact_email: partner?.contact_email ?? '',
        contact_phone: partner?.contact_phone ?? '',
        default_payment_model: partner?.default_payment_model ?? '',
        default_payment_reference: partner?.default_payment_reference ?? '',
    });
    const [companyIds, setCompanyIds] = useState<number[]>(
        partner ? partner.companies.map((c) => c.id) : (prefillCompanyId ? [prefillCompanyId] : []),
    );
    const [busy, setBusy] = useState(false);
    const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));
    const toggleCompany = (id: number) => setCompanyIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

    async function save() {
        if (!f.name.trim() || !f.oib.trim() || !f.iban.trim()) { setError(t('Naziv, OIB i IBAN su obavezni.', 'Name, OIB and IBAN required.')); return; }
        if (companyIds.length === 0) { setError(t('Dodijeli partnera barem jednoj firmi.', 'Assign to at least one company.')); return; }
        setBusy(true);
        try {
            const body = { ...f, company_ids: companyIds };
            const res = partner
                ? await mutate<{ ok: boolean }>(`/admin/partners/${partner.id}`, token, 'PATCH', body).then(() => ({ id: partner.id }))
                : await mutate<{ id: number }>('/admin/partners', token, 'POST', body);
            onSaved((res as { id: number }).id);
        } catch (err) { setError(err instanceof Error ? err.message : 'Error'); } finally { setBusy(false); }
    }

    return (
        <div style={overlay} onClick={onClose}>
            <div style={panel} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ marginTop: 0 }}>🏢 {partner ? t('Uredi partnera', 'Edit partner') : t('Novi partner', 'New partner')}</h3>

                <Field label={t('Naziv *', 'Name *')}><input value={f.name} onChange={(e) => set('name', e.target.value)} style={inp} /></Field>
                <div style={row2}>
                    <Field label="OIB *"><input value={f.oib} onChange={(e) => set('oib', e.target.value)} style={inp} placeholder={t('OIB / porezni broj', 'OIB / tax id')} /></Field>
                    <Field label={t('Grad', 'City')}><input value={f.city} onChange={(e) => set('city', e.target.value)} style={inp} /></Field>
                </div>
                <Field label="IBAN *"><input value={f.iban} onChange={(e) => set('iban', e.target.value)} style={inp} placeholder="HR…" /></Field>
                <div style={row2}>
                    <Field label={t('Banka', 'Bank')}><input value={f.bank_name} onChange={(e) => set('bank_name', e.target.value)} style={inp} /></Field>
                    <Field label={t('Država', 'Country')}><input value={f.country_code} onChange={(e) => set('country_code', e.target.value)} style={inp} maxLength={2} /></Field>
                </div>

                <div style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--ad-ink)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '12px 0 4px' }}>{t('Kontakt osoba', 'Contact person')}</div>
                <Field label={t('Ime i prezime', 'Name')}><input value={f.contact_name} onChange={(e) => set('contact_name', e.target.value)} style={inp} /></Field>
                <div style={row2}>
                    <Field label="Email"><input value={f.contact_email} onChange={(e) => set('contact_email', e.target.value)} style={inp} /></Field>
                    <Field label={t('Telefon', 'Phone')}><input value={f.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} style={inp} /></Field>
                </div>
                <div style={row2}>
                    <Field label={t('Model (HR99)', 'Model')}><input value={f.default_payment_model} onChange={(e) => set('default_payment_model', e.target.value)} style={inp} placeholder="HR99" /></Field>
                    <Field label={t('Poziv na broj (default)', 'Reference (default)')}><input value={f.default_payment_reference} onChange={(e) => set('default_payment_reference', e.target.value)} style={inp} /></Field>
                </div>

                <div style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--ad-ink)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '12px 0 6px' }}>{t('Firme (1 ili više) *', 'Companies (1+) *')}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {companies.map((c) => {
                        const on = companyIds.includes(c.id);
                        return (
                            <button key={c.id} type="button" onClick={() => toggleCompany(c.id)}
                                style={{ cursor: 'pointer', borderRadius: 999, fontSize: 12.5, fontWeight: 600, padding: '5px 12px',
                                    border: on ? '1px solid var(--ad-acc)' : '1px solid #e0d6bf', background: on ? 'rgba(var(--ad-acc-rgb),0.18)' : '#fff', color: on ? 'var(--ad-ink)' : '#8a7a55' }}>
                                {on ? '✓ ' : ''}{c.code}
                            </button>
                        );
                    })}
                </div>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
                    <button onClick={onClose} style={btnGhost}>{t('Odustani', 'Cancel')}</button>
                    <button disabled={busy} onClick={() => void save()} style={{ ...btnGhost, background: 'var(--ad-acc)', color: '#fff', borderColor: 'var(--ad-acc)' }}>{busy ? '…' : t('Spremi', 'Save')}</button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <label style={{ display: 'block', marginBottom: 8 }}>
            <span style={{ display: 'block', fontSize: '0.72rem', color: '#8a7a55', marginBottom: 2 }}>{label}</span>
            {children}
        </label>
    );
}

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(40,28,8,0.34)', zIndex: 1300, display: 'flex', justifyContent: 'flex-end' };
const panel: React.CSSProperties = { width: 560, maxWidth: '94vw', height: '100%', overflowY: 'auto', background: '#fffdf8', boxShadow: '-14px 0 36px -14px rgba(80,50,10,0.45)', padding: '20px 22px', color: '#2f3337' };
const inp: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.88rem' };
const row2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 };
const btnGhost: React.CSSProperties = { background: '#fffdf6', color: '#2f3337', border: '1px solid #d8cdb2', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
